import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { Fragment, type ReactNode, useRef } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Separator } from '@apps/web/components/ui/separator';
import { CallActionsList } from '@apps/web/components/voice_calls/call_actions_list';
import { externalForwardingProviderBadgeLabel } from '@apps/web/components/voice_calls/external_forwarding_provider_utils';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { getDisconnectAttributionLabel } from '@apps/web/components/voice_calls/voice_call_disconnect_attribution';
import {
    formatCallDurationLabel,
    formatRoutingPathLabel,
    formatRoutingReasonLabel,
    isVoiceCallListeningOnly,
} from '@apps/web/components/voice_calls/voice_call_format_utils';
import { formatVoiceCallOutreachLinkLabel } from '@apps/web/components/voice_calls/voice_call_outreach_utils';
import {
    getVoiceCallTransferLegs,
    VoiceCallPathTrace,
} from '@apps/web/components/voice_calls/voice_call_path_trace';
import {
    buildVoiceCallTranscriptViewModel,
    VoiceCallTranscriptCard,
} from '@apps/web/components/voice_calls/voice_call_transcript_card';
import { hasVoiceCallVoicemailBadge } from '@apps/web/components/voice_calls/voice_call_voicemail_utils';
import { VoiceCallsTranscript } from '@apps/web/components/voice_calls/voice_calls_transcript';
import {
    formatNationalPhoneNumber,
    isS3RecordingUrl,
} from '@apps/web/lib/contact_utils';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type OrganizationVoiceCall = ApiResponseType<
    '/organizations/{organization_id}/voice-calls/{voice_call_id}',
    'get'
>;

/**
 * Two-column call detail layout: transcript on the left, metadata aside on
 * the right, stacking when there isn't room for both.
 *
 * The split is driven by container queries rather than viewport breakpoints
 * because every host pane is much narrower than the window — a 360px list
 * split on the voice calls page, a dialog, the activity feed context panel.
 * With viewport breakpoints a ~480px pane still matched `xl` and reserved an
 * 18rem aside, leaving the summary a couple of words per line and pushing the
 * transfer badge over the aside.
 *
 * The 36rem threshold keeps two columns for as long as they stay readable
 * (the transcript still gets ~19rem beside a 16rem aside) and only stacks
 * below that, where the aside would squeeze the transcript to a few words per
 * line rather than merely making it narrow.
 */
export function VoiceCallDetailLayout({
    main,
    aside,
}: {
    readonly main: ReactNode;
    readonly aside: ReactNode;
}) {
    return (
        <div className="@container/call-detail">
            <div className="grid gap-5 @xl/call-detail:grid-cols-[minmax(0,1fr)_16rem] @5xl/call-detail:grid-cols-[minmax(0,44rem)_18rem]">
                <div className="min-w-0 space-y-4 @xl/call-detail:col-start-1 @xl/call-detail:row-start-1">
                    {main}
                </div>
                <aside
                    aria-label="Call details"
                    className="min-w-0 space-y-4 border-t pt-4 @xl/call-detail:sticky @xl/call-detail:top-0 @xl/call-detail:col-start-2 @xl/call-detail:row-start-1 @xl/call-detail:border-t-0 @xl/call-detail:border-l @xl/call-detail:pt-0 @xl/call-detail:pl-5"
                >
                    {aside}
                </aside>
            </div>
        </div>
    );
}

export function VoiceCallDetailMainContent({
    organizationId,
    call,
    timezone,
    isAdmin,
    prefetchedRecordingUrl,
    isRecordingUrlLoading,
    recordingUrlError,
    flatTranscript = false,
    silentListeningLabelSettingsEnabled = false,
}: {
    readonly organizationId: string;
    readonly call: OrganizationVoiceCall;
    readonly timezone: string;
    readonly isAdmin: boolean;
    readonly prefetchedRecordingUrl?: string;
    readonly isRecordingUrlLoading?: boolean;
    readonly recordingUrlError?: unknown;
    // The call dialog renders the voice-calls-page presentation: a flat
    // seekable transcript with the recording player and no summary or card
    // chrome. Embedded panes keep the compact transcript card.
    readonly flatTranscript?: boolean;
    readonly silentListeningLabelSettingsEnabled?: boolean;
}) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const transcriptViewModel = buildVoiceCallTranscriptViewModel({
        call,
        timestampLabel: formatDateAndTime(
            call.started_at ?? call.created_at,
            timezone,
        ),
        transcriptText: call.transcript,
        translation: call.translation,
        silentListeningLabelSettingsEnabled,
    });

    function handleSeek(seconds: number) {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        audio.currentTime = seconds;
        // play() can reject under the browser autoplay policy or before the
        // media is ready; ignore the rejection so it isn't an unhandled error.
        audio.play().catch(() => {});
    }

    // Only make transcript lines clickable-to-seek when the player actually
    // renders an <audio> element. For S3 recordings the player shows a status
    // message until the signed URL arrives, during which there is no element
    // to seek, so clicks would silently do nothing.
    const hasPlayableRecording =
        !!prefetchedRecordingUrl ||
        (!!call.recording_url && !isS3RecordingUrl(call.recording_url));

    return (
        <Fragment>
            {isAdmin ? (
                <div className="flex justify-end">
                    <a
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        href={`/admin/voice-qa/${call.id}`}
                    >
                        Open in Voice QA
                        <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            ) : null}
            {call.actions.some((action) => action.type !== 'transfer') && (
                <div className="space-y-2">
                    <h3 className="text-sm font-medium">Actions</h3>
                    <CallActionsList
                        actions={call.actions}
                        organizationId={organizationId}
                    />
                </div>
            )}
            {flatTranscript ? (
                <VoiceCallsTranscript
                    afterSummary={
                        call.recording_url ? (
                            <div className="bg-muted rounded-xl px-2 py-1.5">
                                <VoiceCallAudioPlayer
                                    audioRef={audioRef}
                                    className="h-9 w-full focus:outline-none"
                                    isRecordingUrlLoading={
                                        isRecordingUrlLoading
                                    }
                                    prefetchedRecordingUrl={
                                        prefetchedRecordingUrl
                                    }
                                    recordingUrlError={recordingUrlError}
                                    voiceCall={call}
                                />
                            </div>
                        ) : undefined
                    }
                    disconnectAttribution={call.disconnect_attribution}
                    onSeek={hasPlayableRecording ? handleSeek : undefined}
                    showSummary={false}
                    viewModel={transcriptViewModel}
                />
            ) : transcriptViewModel.hasContent ? (
                <VoiceCallTranscriptCard
                    className="w-full"
                    isRecordingUrlLoading={isRecordingUrlLoading}
                    prefetchedRecordingUrl={prefetchedRecordingUrl}
                    recordingUrlError={recordingUrlError}
                    viewModel={transcriptViewModel}
                    voiceCall={call}
                />
            ) : (
                <div className="space-y-2">
                    <Separator />
                    <p className="text-muted-foreground text-sm">
                        No recording, summary, or transcript is available for
                        this call.
                    </p>
                </div>
            )}
        </Fragment>
    );
}

export function VoiceCallDetailsPanel({
    call,
    caregiverName,
    showOutreachLink = true,
    timezone,
    silentListeningLabelSettingsEnabled = false,
}: {
    readonly call: OrganizationVoiceCall;
    readonly caregiverName?: string | null;
    readonly showOutreachLink?: boolean;
    readonly timezone: string;
    readonly silentListeningLabelSettingsEnabled?: boolean;
}) {
    const isInboundCall = call.direction === 'inbound';
    const isListeningOnly = isVoiceCallListeningOnly(
        call,
        silentListeningLabelSettingsEnabled,
    );
    const dialedOfficeLine =
        (isInboundCall && call.inbound_external_forwarding_main_number) || null;
    const toNumberDisplay = formatNationalPhoneNumber(
        dialedOfficeLine ?? call.to_number,
    );
    const forwardedNumberDisplay = formatNationalPhoneNumber(call.to_number);
    const participantLabel = isInboundCall ? 'Caller' : 'Recipient';
    const unknownParticipantLabel = isInboundCall
        ? 'Unknown caller'
        : 'Unknown recipient';
    const participantDisplayName = caregiverName?.trim() || null;
    const participantPhoneFallback = formatNationalPhoneNumber(
        isInboundCall ? call.from_number : call.to_number,
    );
    const hasVoicemailFlag = hasVoiceCallVoicemailBadge(call);
    const forwardingProviderLabel = externalForwardingProviderBadgeLabel(
        call.external_forwarding_provider,
    );
    const simulation = call.simulation ?? null;
    // Inbound calls show transfers inside the call path; outbound calls have no
    // call path, so surface the transfer destination on its own here.
    const outboundTransferLegs = isInboundCall
        ? []
        : getVoiceCallTransferLegs(call);
    const outreachLinkLabel =
        showOutreachLink && !isInboundCall && call.callout_id
            ? formatVoiceCallOutreachLinkLabel({
                  outreachType: call.outreach_type,
                  outreachClientNames: call.outreach_client_names,
                  earliestShiftStartTime:
                      call.outreach_earliest_shift_start_time,
                  shiftCount: call.outreach_shift_count,
                  timezone,
              })
            : null;
    const disconnectAttribution = call.disconnect_attribution;
    const callerEndedLabel =
        disconnectAttribution?.hangup_actor === 'caller'
            ? getDisconnectAttributionLabel(disconnectAttribution)
            : null;

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <p className="text-muted-foreground text-xs">
                    {participantLabel}
                </p>
                <p className="text-sm font-medium break-words">
                    {participantDisplayName ??
                        participantPhoneFallback ??
                        unknownParticipantLabel}
                </p>
            </div>
            <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Started</p>
                <p className="text-sm font-medium">
                    {formatDateAndTime(
                        call.started_at ?? call.created_at,
                        timezone,
                    )}
                </p>
            </div>
            <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Duration</p>
                <p className="text-sm font-medium">
                    {formatCallDurationLabel(call.duration_ms) ?? '—'}
                </p>
            </div>
            <div className="space-y-1">
                <p className="text-muted-foreground text-xs">Tags</p>
                <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">
                        {isInboundCall ? 'Inbound' : 'Outbound'}
                    </Badge>
                    {(call.transferred_to_back_office || isListeningOnly) && (
                        <Badge variant="secondary">
                            {isListeningOnly ? 'Listening only' : 'Transferred'}
                        </Badge>
                    )}
                    {forwardingProviderLabel && (
                        <Badge variant="secondary">
                            {forwardingProviderLabel}
                        </Badge>
                    )}
                    {hasVoicemailFlag && (
                        <Badge variant="secondary">Voicemail</Badge>
                    )}
                    {simulation ? (
                        <Badge variant="secondary">{simulation.label}</Badge>
                    ) : null}
                </div>
                {simulation?.detail ? (
                    <p className="text-muted-foreground text-xs">
                        {simulation.detail}
                    </p>
                ) : null}
            </div>
            <div className="space-y-1">
                <p className="text-muted-foreground text-xs">From</p>
                <p className="font-mono text-sm break-all">
                    {formatNationalPhoneNumber(call.from_number) || '—'}
                </p>
                {isInboundCall && participantDisplayName ? (
                    <p
                        className="text-muted-foreground text-xs break-words"
                        data-testid="from-participant-name"
                    >
                        {participantDisplayName}
                    </p>
                ) : null}
            </div>
            <div className="space-y-1">
                <p className="text-muted-foreground text-xs">To</p>
                <p className="font-mono text-sm break-all">
                    {toNumberDisplay || '—'}
                </p>
                {dialedOfficeLine && call.to_number ? (
                    <p
                        className="text-muted-foreground text-xs break-all"
                        data-testid="to-forwarded-number"
                    >
                        {isListeningOnly
                            ? `Passed through Phoebe’s listening line ${forwardedNumberDisplay}`
                            : `Forwarded to ${forwardedNumberDisplay}`}
                    </p>
                ) : null}
                {!isInboundCall && participantDisplayName ? (
                    <p
                        className="text-muted-foreground text-xs break-words"
                        data-testid="to-participant-name"
                    >
                        {participantDisplayName}
                    </p>
                ) : null}
            </div>
            {isInboundCall ? (
                <div className="space-y-1">
                    <p className="text-muted-foreground text-xs">Routing</p>
                    <p className="text-sm font-medium">
                        {call.routing_path
                            ? formatRoutingPathLabel(call.routing_path)
                            : '—'}
                    </p>
                </div>
            ) : null}
            {isInboundCall ? (
                <VoiceCallPathTrace
                    call={call}
                    callerEndedLabel={callerEndedLabel}
                    silentListeningLabelSettingsEnabled={
                        silentListeningLabelSettingsEnabled
                    }
                />
            ) : null}
            {outboundTransferLegs.map((leg, index) => (
                <div key={index} className="space-y-1">
                    <p className="text-muted-foreground text-xs">
                        Transferred to
                    </p>
                    <p className="text-sm font-medium">
                        {leg.destination_label ?? 'Unknown destination'}
                    </p>
                    {leg.transfer_number ? (
                        <p className="text-muted-foreground font-mono text-xs break-all">
                            {formatNationalPhoneNumber(leg.transfer_number)}
                        </p>
                    ) : null}
                </div>
            ))}
            {outreachLinkLabel && call.callout_id && (
                <div className="space-y-1">
                    <p className="text-muted-foreground text-xs">Outreach</p>
                    <Link
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        to="/outreaches/$callout-id"
                        params={{
                            'callout-id': call.callout_id,
                        }}
                    >
                        {outreachLinkLabel}
                    </Link>
                </div>
            )}
            {call.routing_reason ? (
                <div className="space-y-4 border-t pt-4">
                    <div className="space-y-1">
                        <p className="text-muted-foreground text-xs">
                            Routing reason
                        </p>
                        <p className="text-sm font-medium">
                            {formatRoutingReasonLabel(
                                call.routing_reason,
                                call.routing_schedule_name,
                            )}
                        </p>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function CallDetailView({
    organizationId,
    call,
    timezone,
    caregiverName,
    isAdmin,
    prefetchedRecordingUrl,
    isRecordingUrlLoading,
    recordingUrlError,
    flatTranscript = false,
    silentListeningLabelSettingsEnabled = false,
}: {
    readonly organizationId: string;
    readonly call: OrganizationVoiceCall;
    readonly timezone: string;
    readonly caregiverName?: string | null;
    readonly isAdmin: boolean;
    readonly prefetchedRecordingUrl?: string;
    readonly isRecordingUrlLoading?: boolean;
    readonly recordingUrlError?: unknown;
    readonly flatTranscript?: boolean;
    readonly silentListeningLabelSettingsEnabled?: boolean;
}) {
    return (
        <VoiceCallDetailLayout
            main={
                <VoiceCallDetailMainContent
                    call={call}
                    flatTranscript={flatTranscript}
                    isAdmin={isAdmin}
                    isRecordingUrlLoading={isRecordingUrlLoading}
                    organizationId={organizationId}
                    prefetchedRecordingUrl={prefetchedRecordingUrl}
                    recordingUrlError={recordingUrlError}
                    silentListeningLabelSettingsEnabled={
                        silentListeningLabelSettingsEnabled
                    }
                    timezone={timezone}
                />
            }
            aside={
                <VoiceCallDetailsPanel
                    call={call}
                    caregiverName={caregiverName}
                    silentListeningLabelSettingsEnabled={
                        silentListeningLabelSettingsEnabled
                    }
                    timezone={timezone}
                />
            }
        />
    );
}
